import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import { apiClient } from "../../utils/apiClient";
import { ActionLogTableRow } from "../actionLog/ActionLogTableRow";
import type { ActionLogInfo } from "@services/admin/types";
import type { AuditLogApiRequest } from "@services/shipments/types";
import { useActionLogHandlers } from "../hooks/useActionLogHandlers";

interface AuditModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
}

export const ShipmentAuditModal = React.memo(function ShipmentAuditModal({
  open,
  onClose,
  shipmentId,
}: AuditModalProps) {
  const [actionLogs, setActionLogs] = useState<ActionLogInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use the reusable action log handlers hook
  const {
    handleViewEmail,
    handleViewDetails,
    handleViewGptRequest,
    handleViewExtractedData,
    handleViewDerivedData,
    handleViewShipmentEmail,
    handleViewShipmentFeed,
    handleViewAIAgentTrace,
    handleViewErrors,
    handleViewReminder,
  } = useActionLogHandlers();

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const requestBody: AuditLogApiRequest = {
        shipmentId,
      };

      const response = await apiClient.post<ActionLogInfo[]>(
        "/shipments/audit-log",
        requestBody
      );

      // Filter for relevant action types (already done on backend, but double-check)
      const relevantLogs = response.filter((log) =>
        ["CreateShipment", "UpdateShipment", "MergeShipment"].includes(log.type)
      );

      setActionLogs(relevantLogs);
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      setError("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    if (open && shipmentId) {
      fetchAuditLogs();
    }
  }, [open, shipmentId, fetchAuditLogs]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: "80vh" },
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          Audit Log for Shipment {shipmentId}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </div>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : actionLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No audit logs found for this shipment.
          </Typography>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table stickyHeader>
              <TableBody>
                {actionLogs.map((log) => (
                  <ActionLogTableRow
                    key={log.id}
                    log={log}
                    includeDescription={true}
                    displayErrors={true}
                    onViewDetails={handleViewDetails}
                    onViewGptRequest={handleViewGptRequest}
                    onViewExtractedData={handleViewExtractedData}
                    onViewDerivedData={handleViewDerivedData}
                    onViewShipmentEmail={handleViewShipmentEmail}
                    onViewShipmentFeed={handleViewShipmentFeed}
                    onViewAIAgentTrace={handleViewAIAgentTrace}
                    onViewErrors={handleViewErrors}
                    onViewEmail={handleViewEmail}
                    onViewReminder={handleViewReminder}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
});
